class ProductMediaGrid extends HTMLElement {
  constructor() {
    super();
    this.toggleButton = this.querySelector('[data-media-grid-toggle]');
    this.imageWrapper = this.querySelector('[data-media-grid-wrapper]');
    this.labels = {
      more: window.themeVariables.customiser.mediaGrid.viewMore,
      less: window.themeVariables.customiser.mediaGrid.viewLess
    };

    this.contentHeight = 0;
    this.isAnimating = false;
    this.primaryMediaAnimation = null;
    this.selectedVariantMediaId = null;
    this.requestedVariantMediaId = null;

    this.onToggle = this.onToggle.bind(this);
    this.measure = this.measure.bind(this);
    this.onTransitionEnd = this.onTransitionEnd.bind(this);
    this.onVariantChanged = this.onVariantChanged.bind(this);
  }

  connectedCallback() {
    const initialMediaId = this.querySelector('[data-media-id]')?.dataset.mediaId;

    this.selectedVariantMediaId = initialMediaId ? Number(initialMediaId) : null;
    this.requestedVariantMediaId = this.selectedVariantMediaId;
    document.addEventListener('variant:changed', this.onVariantChanged);

    if (!this.toggleButton || !this.imageWrapper) return;

    // Initial measure after images decode
    const media = this.imageWrapper.querySelectorAll('img, video, model');
    Promise.all(
      Array.from(media).map((img) =>
        img.complete ? 1 : img.decode().catch(() => {}),
      ),
    ).then(this.measure);
    window.addEventListener('load', this.measure, { once: true });

    // Observe size changes, but skip while we're animating
    this.resizeObserver = new ResizeObserver(() => {
      if (!this.isAnimating) this.measure();
    });
    this.resizeObserver.observe(this.imageWrapper);

    this.toggleButton.addEventListener('click', this.onToggle);
    this.imageWrapper.addEventListener('transitionend', this.onTransitionEnd);
  }

  disconnectedCallback() {
    this.primaryMediaAnimation?.cancel();
    this.requestedVariantMediaId = null;
    document.removeEventListener('variant:changed', this.onVariantChanged);
    this.toggleButton?.removeEventListener('click', this.onToggle);
    this.imageWrapper?.removeEventListener(
      'transitionend',
      this.onTransitionEnd,
    );
    this.resizeObserver?.disconnect();
  }

  onVariantChanged(event) {
    const productRerender = this.closest('product-rerender');
    const observedFormId = productRerender?.getAttribute('observe-form');
    const eventFormId = event.target?.getAttribute?.('id');
    const featuredMediaId = Number(
      event.detail?.variant?.featured_media?.id,
    );

    if (
      eventFormId !== observedFormId ||
      !featuredMediaId ||
      featuredMediaId === this.requestedVariantMediaId
    ) {
      return;
    }

    this.requestedVariantMediaId = featuredMediaId;

    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    if (window.matchMedia('(width >= 1000px)').matches) {
      this.swapPrimaryMedia(featuredMediaId, !reducedMotion);
      return;
    }

    this.selectMedia(featuredMediaId, !reducedMotion);
  }

  async swapPrimaryMedia(mediaId, animate = true) {
    let targetMedia = this.imageWrapper?.querySelector(
      `[data-media-id="${mediaId}"]`,
    );

    if (!targetMedia) return;

    const targetImage = targetMedia.querySelector('img');

    if (targetImage) {
      if (!targetImage.complete || !targetImage.naturalWidth) {
        targetImage.loading = 'eager';
      }

      if (typeof targetImage.decode === 'function') {
        await targetImage.decode().catch(() => {});
      }
    }

    if (this.requestedVariantMediaId !== Number(mediaId)) return;

    const primaryMedia = this.imageWrapper?.firstElementChild;
    targetMedia = this.imageWrapper?.querySelector(
      `[data-media-id="${mediaId}"]`,
    );

    if (!primaryMedia || !targetMedia) return;

    this.primaryMediaAnimation?.cancel();
    this.primaryMediaAnimation = null;

    if (primaryMedia === targetMedia) {
      this.selectedVariantMediaId = Number(mediaId);
      return;
    }

    const primaryLayout = {
      index: primaryMedia.dataset.index,
      isLarge: primaryMedia.classList.contains('product-media--large'),
    };
    const targetLayout = {
      index: targetMedia.dataset.index,
      isLarge: targetMedia.classList.contains('product-media--large'),
    };
    const primaryMarker = document.createComment('primary-media');
    const targetMarker = document.createComment('target-media');

    primaryMedia.replaceWith(primaryMarker);
    targetMedia.replaceWith(targetMarker);
    primaryMarker.replaceWith(targetMedia);
    targetMarker.replaceWith(primaryMedia);

    this.applyMediaLayout(targetMedia, primaryLayout);
    this.applyMediaLayout(primaryMedia, targetLayout);
    this.selectedVariantMediaId = Number(mediaId);

    if (animate) {
      this.primaryMediaAnimation = targetMedia.animate(
        { opacity: [0, 1] },
        { duration: 250, easing: 'ease-in-out' },
      );
    }
  }

  applyMediaLayout(media, layout) {
    media.dataset.index = layout.index;
    media.classList.toggle('product-media--large', layout.isLarge);
  }

  selectMedia(mediaId, animate = true) {
    if (!window.matchMedia('(width < 1000px)').matches || !mediaId) return;

    const targetMedia = this.querySelector(
      `[data-media-id="${mediaId}"]`,
    );

    if (!targetMedia) return;

    targetMedia.scrollIntoView({
      behavior: animate ? 'smooth' : 'auto',
      block: 'nearest',
      inline: 'center',
    });

    this.selectedVariantMediaId = Number(mediaId);
  }

  measure() {
    // DO NOT clear max-height here; scrollHeight still reflects full content
    this.contentHeight = this.imageWrapper.scrollHeight;
  }

  onToggle() {
    const expanded =
      this.imageWrapper.style.maxHeight &&
      this.imageWrapper.style.maxHeight !== 'none';

    // Guard: capture current content size before starting the animation
    this.measure();
    this.isAnimating = true;

    if (expanded) {
      // Collapse
      // Ensure a numeric value is set first so the transition has a start point
      this.imageWrapper.style.maxHeight = `${this.imageWrapper.scrollHeight}px`;
      // Force reflow so the browser recognizes the start value before changing it

      this.imageWrapper.offsetHeight;
      this.imageWrapper.style.maxHeight = '';
      this.toggleButton.textContent = this.labels.more;
      // Optionally scroll into view after collapse
      this.scrollIntoView(true, { behavior: 'smooth', block: 'start' });
    } else {
      // Expand
      this.imageWrapper.style.maxHeight = `${this.contentHeight}px`;
      this.toggleButton.textContent = this.labels.less;
    }
  }

  onTransitionEnd(evt) {
    if (evt.propertyName !== 'max-height') return;

    this.isAnimating = false;

    const expanded = this.toggleButton.textContent === this.labels.less;

    if (expanded) {
      // Let content grow naturally after expansion
      this.imageWrapper.classList.add('product-media-grid__overflow--expanded');
    } else {
      this.imageWrapper.classList.remove('product-media-grid__overflow--expanded');
    }
  }
}

if (!customElements.get('product-media-grid')) {
  customElements.define('product-media-grid', ProductMediaGrid);
}
